import React from "react";
import { useNavigate } from "react-router-dom";
import Button from "../../../components/ui/Button";
import { MessageCircleDashed } from "lucide-react";
import Eyebrow from "./Eyebrow";
import useNavigateToFibiVoice from "../../../../../hooks/useNavigateToFibiVoice";

// ── SVG Icons ──────────────────────────────────────────────────────────────

const IconBubbles = ({
  size = 24,
  color = "currentColor",
}: {
  size?: number;
  color?: string;
}) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke={color}
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M7.001 15.085A1.5 1.5 0 0 1 9 16.5" />
    <circle cx="18.5" cy="8.5" r="3.5" />
    <circle cx="7.5" cy="16.5" r="5.5" />
    <circle cx="7.5" cy="4.5" r="2.5" />
  </svg>
);

const IconBrainCircuit = ({
  size = 24,
  color = "currentColor",
}: {
  size?: number;
  color?: string;
}) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke={color}
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M12 5a3 3 0 1 0-5.997.125 4 4 0 0 0-2.526 5.77 4 4 0 0 0 .556 6.588A4 4 0 1 0 12 18Z" />
    <path d="M9 13a4.5 4.5 0 0 0 3-4" />
    <path d="M6.003 5.125A3 3 0 0 0 6.401 6.5" />
    <path d="M3.477 10.896a4 4 0 0 1 .585-.396" />
    <path d="M6 18a4 4 0 0 1-1.967-.516" />
    <path d="M12 13h4" />
    <path d="M12 18h6a2 2 0 0 1 2 2v1" />
    <path d="M12 8h8" />
    <path d="M16 8V5a2 2 0 0 1 2-2" />
    <circle cx="16" cy="13" r=".5" />
    <circle cx="18" cy="3" r=".5" />
    <circle cx="20" cy="21" r=".5" />
    <circle cx="20" cy="8" r=".5" />
  </svg>
);

const IconClipboardClock = ({
  size = 24,
  color = "currentColor",
}: {
  size?: number;
  color?: string;
}) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke={color}
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M16 14v2.2l1.6 1" />
    <path d="M16 4h2a2 2 0 0 1 2 2v.832" />
    <path d="M8 4H6a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h2" />
    <circle cx="16" cy="16" r="6" />
    <rect x="8" y="2" width="8" height="4" rx="1" />
  </svg>
);

const IconBrainCog = ({
  size = 24,
  color = "currentColor",
}: {
  size?: number;
  color?: string;
}) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke={color}
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="m10.852 14.772-.383.923" />
    <path d="m10.852 9.228-.383-.923" />
    <path d="m13.148 14.772.382.924" />
    <path d="m13.531 8.305-.383.923" />
    <path d="m14.772 10.852.923-.383" />
    <path d="m14.772 13.148.923.383" />
    <path d="M17.598 6.5A3 3 0 1 0 12 5a3 3 0 0 0-5.63-1.446 3 3 0 0 0-.368 1.571 4 4 0 0 0-2.525 5.771" />
    <path d="M17.998 5.125a4 4 0 0 1 2.525 5.771" />
    <path d="M19.505 10.294a4 4 0 0 1-1.5 7.706" />
    <path d="M4.032 17.483A4 4 0 0 0 11.464 20c.18-.311.892-.311 1.072 0a4 4 0 0 0 7.432-2.516" />
    <path d="M4.5 10.291A4 4 0 0 0 6 18" />
    <path d="M6.002 5.125a3 3 0 0 0 .4 1.375" />
    <path d="m9.228 10.852-.923-.383" />
    <path d="m9.228 13.148-.923.383" />
    <circle cx="12" cy="12" r="3" />
  </svg>
);

// ── Background Artifact Patterns ───────────────────────────────────────────

const DotPattern = ({ color }: { color: string }) => (
  <svg
    style={{
      position: "absolute",
      bottom: 0,
      right: 0,
      opacity: 0.18,
      pointerEvents: "none",
    }}
    width="360"
    height="125"
    viewBox="0 0 360 125"
    xmlns="http://www.w3.org/2000/svg"
  >
    {Array.from({ length: 5 }, (_, row) =>
      Array.from({ length: 16 }, (_, col) => (
        <circle
          key={`${row}-${col}`}
          cx={col * 22 + 10}
          cy={row * 22 + 10}
          r="2.5"
          fill={color}
        />
      )),
    )}
  </svg>
);

const WavePattern = ({ color }: { color: string }) => (
  <svg
    style={{
      position: "absolute",
      bottom: 0,
      right: 0,
      opacity: 0.13,
      pointerEvents: "none",
    }}
    width="175"
    height="113"
    viewBox="0 0 175 113"
    xmlns="http://www.w3.org/2000/svg"
  >
    {[0, 20, 40, 60, 80].map((y, i) => (
      <path
        key={i}
        d={`M0 ${y + 14} Q44 ${y} 88 ${y + 14} Q131 ${y + 28} 175 ${y + 14}`}
        fill="none"
        stroke={color}
        strokeWidth="2"
      />
    ))}
  </svg>
);

const GridPattern = ({ color }: { color: string }) => (
  <svg
    style={{
      position: "absolute",
      bottom: 0,
      right: 0,
      opacity: 0.12,
      pointerEvents: "none",
    }}
    width="138"
    height="113"
    viewBox="0 0 138 113"
    xmlns="http://www.w3.org/2000/svg"
  >
    {[0, 18, 36, 54, 72, 90, 108].map((y, i) => (
      <line
        key={`h${i}`}
        x1="0"
        y1={y}
        x2="138"
        y2={y}
        stroke={color}
        strokeWidth="1.2"
      />
    ))}
    {[0, 18, 36, 54, 72, 90, 108, 126].map((x, i) => (
      <line
        key={`v${i}`}
        x1={x}
        y1="0"
        x2={x}
        y2="113"
        stroke={color}
        strokeWidth="1.2"
      />
    ))}
  </svg>
);

const CircuitPattern = ({ color }: { color: string }) => (
  <svg
    style={{
      position: "absolute",
      bottom: 0,
      right: 0,
      opacity: 0.14,
      pointerEvents: "none",
    }}
    width="520"
    height="125"
    viewBox="0 0 520 125"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      d="M10 100 L10 65 L55 65 L55 30 L100 30"
      fill="none"
      stroke={color}
      strokeWidth="1.5"
    />
    <path
      d="M80 100 L80 75 L130 75 L130 40 L180 40"
      fill="none"
      stroke={color}
      strokeWidth="1.5"
    />
    <path
      d="M150 100 L150 65 L210 65"
      fill="none"
      stroke={color}
      strokeWidth="1.5"
    />
    <path
      d="M230 100 L230 55 L285 55 L285 25 L340 25"
      fill="none"
      stroke={color}
      strokeWidth="1.5"
    />
    <path
      d="M310 100 L310 70 L370 70 L370 35 L430 35"
      fill="none"
      stroke={color}
      strokeWidth="1.5"
    />
    <path
      d="M400 100 L400 60 L460 60 L460 30 L510 30"
      fill="none"
      stroke={color}
      strokeWidth="1.5"
    />
    <circle cx="55" cy="65" r="3.5" fill={color} />
    <circle cx="130" cy="75" r="3.5" fill={color} />
    <circle cx="10" cy="65" r="3.5" fill={color} />
    <circle cx="285" cy="55" r="3.5" fill={color} />
    <circle cx="370" cy="70" r="3.5" fill={color} />
    <circle cx="460" cy="60" r="3.5" fill={color} />
    <rect
      x="96"
      y="25"
      width="8"
      height="10"
      rx="1"
      fill="none"
      stroke={color}
      strokeWidth="1.5"
    />
    <rect
      x="176"
      y="35"
      width="8"
      height="10"
      rx="1"
      fill="none"
      stroke={color}
      strokeWidth="1.5"
    />
    <rect
      x="336"
      y="20"
      width="8"
      height="10"
      rx="1"
      fill="none"
      stroke={color}
      strokeWidth="1.5"
    />
    <rect
      x="426"
      y="30"
      width="8"
      height="10"
      rx="1"
      fill="none"
      stroke={color}
      strokeWidth="1.5"
    />
    <rect
      x="506"
      y="25"
      width="8"
      height="10"
      rx="1"
      fill="none"
      stroke={color}
      strokeWidth="1.5"
    />
  </svg>
);

const STEPS = [
  {
    n: "01",
    Icon: IconBubbles,
    title: "Personalized Question Generation",
    desc: "FiBi analyzes your visa type, profile, and case details to generate authentic consular questions tailored to you.",
    color: "#a881db",
    bgColor: "rgba(168,129,219,0.08)",
    tags: ["F1/F2", "B1/B2", "M1/M2", "J1/J2"],
    Pattern: DotPattern,
  },
  {
    n: "02",
    Icon: IconBrainCircuit,
    title: "Realistic Simulation",
    desc: "Voice and text modes replicate the real interview environment. Choose your pace and modality freely mid-session.",
    color: "#0076e9",
    bgColor: "rgba(0,118,233,0.07)",
    Pattern: WavePattern,
  },
  {
    n: "03",
    Icon: IconClipboardClock,
    title: "Instant Feedback",
    desc: "Immediate AI-powered scoring on clarity, confidence, and credibility — evaluated in real time after each answer.",
    color: "#00c1a3",
    bgColor: "rgba(0,193,163,0.07)",
    Pattern: GridPattern,
  },
  {
    n: "04",
    Icon: IconBrainCog,
    title: "Adaptive Learning",
    desc: "FiBi's adaptive engine strengthens weak areas and progressively raises the challenge — maximising your readiness score.",
    color: "#feb204",
    bgColor: "rgba(254,178,4,0.07)",
    Pattern: CircuitPattern,
  },
];

const HowItWorks: React.FC = () => {
  const navigate = useNavigate();
  const { url: fibiUrl } = useNavigateToFibiVoice();

  return (
    <>
      <style>{`
        .hiw-section { padding: 88px 60px; }
        .hiw-grid {
          display: grid;
          grid-template-columns: repeat(3, 1fr);
          gap: 14px;
          margin-bottom: 52px;
        }
        .hiw-card-span2 { grid-column: span 2; }
        .hiw-card1-inner { display: flex; gap: 28px; align-items: flex-start; }

        @media (max-width: 900px) {
          .hiw-section { padding: 64px 28px; }
          .hiw-grid { grid-template-columns: repeat(2, 1fr); }
          .hiw-card-span2 { grid-column: span 2; }
        }

        @media (max-width: 600px) {
          .hiw-section { padding: 48px 16px; }
          .hiw-grid { grid-template-columns: 1fr; }
          .hiw-card-span2 { grid-column: span 1; }
          .hiw-card1-inner { flex-direction: column; gap: 16px; }
        }
      `}</style>
      <section
        className="hiw-section"
        style={{
          background:
            "linear-gradient(180deg, rgba(240,246,255,0.95) 0%, #f9fcff 65%)",
        }}
      >
        <div style={{ maxWidth: 1200, margin: "0 auto" }}>
          <div style={{ textAlign: "center", marginBottom: 72 }}>
            <Eyebrow color="#a881db">
              <MessageCircleDashed className="h-4 w-4" /> Meet FiBi!
            </Eyebrow>
            <div
              style={{
                maxWidth: 850,
                margin: "0 auto",
                aspectRatio: "16/9",
                borderRadius: 24,
                overflow: "hidden",
                border: "1px solid #ebe8e8",
                background:
                  "repeating-linear-gradient(45deg,#f3f4f6,#f3f4f6 12px,#eaecf0 12px,#eaecf0 24px)",
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                gap: 14,
                cursor: "pointer",
                boxShadow: "0 4px 16px rgba(0,0,0,0.06)",
                padding: 8,
              }}
            >
              <div className="relative w-full h-full overflow-hidden rounded-2xl">
                <iframe
                  className="absolute inset-0 w-full h-full"
                  src="https://www.youtube.com/embed/CKJp2CoZSOY?si=UOnKlJjsMBU5p_Ps&controls=1"
                  title="FiBi AI demo video"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                  referrerPolicy="strict-origin-when-cross-origin"
                  allowFullScreen
                />
              </div>
            </div>
          </div>

          {/* Section heading */}
          <div style={{ textAlign: "center", marginBottom: 52 }}>
            <Eyebrow color="#0076e9">✦ How It Works</Eyebrow>
            <h2
              style={{
                fontSize: 44,
                fontWeight: 300,
                letterSpacing: "-0.02em",
                color: "#1a1a1a",
                marginBottom: 14,
              }}
            >
              Four Steps to
              <span className="text-blue-00 italic mx-1">Mastery</span>
            </h2>
            <p
              style={{
                color: "#6b6d70",
                fontSize: 16,
                maxWidth: "52ch",
                margin: "0 auto",
              }}
            >
              FiBi's intelligent engine prepares you end-to-end — from question
              generation to adaptive mastery.
            </p>
          </div>

          {/* Bento grid */}
          <div className="hiw-grid">
            {/* Card 1: Personalized Question Generation (spans 2 cols) */}
            <div
              className="bento-card hiw-card-span2"
              style={{
                background: "#ffffff",
                border: "1.5px solid #ebe8e8",
                borderRadius: 20,
                padding: 32,
                boxShadow: "0 4px 16px rgba(0,0,0,0.06)",
                position: "relative",
                overflow: "hidden",
              }}
            >
              <DotPattern color={STEPS[0].color} />
              <div className="hiw-card1-inner">
                <div
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    flexShrink: 0,
                  }}
                >
                  <div
                    style={{
                      width: 60,
                      height: 60,
                      borderRadius: 16,
                      background: STEPS[0].bgColor,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      marginBottom: 12,
                    }}
                  >
                    <IconBubbles size={26} color={STEPS[0].color} />
                  </div>
                  <span
                    style={{
                      fontSize: 24,
                      fontWeight: 900,
                      color: STEPS[0].color,
                    }}
                  >
                    01
                  </span>
                </div>
                <div style={{ position: "relative", zIndex: 1 }}>
                  <h3
                    style={{
                      fontSize: 18,
                      fontWeight: 700,
                      color: "#1a1a1a",
                      marginBottom: 8,
                    }}
                  >
                    Personalized Question Generation
                  </h3>
                  <p
                    style={{
                      fontSize: 14,
                      color: "#6b6d70",
                      lineHeight: 1.6,
                      marginBottom: 16,
                    }}
                  >
                    FiBi analyzes your visa type, profile, and case details to
                    generate authentic consular questions tailored to you.
                  </p>
                  <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                    {STEPS[0].tags?.map((tag) => (
                      <span
                        key={tag}
                        style={{
                          fontSize: 11,
                          fontWeight: 700,
                          color: "#a881db",
                          background: "rgba(168,129,219,0.1)",
                          padding: "4px 10px",
                          borderRadius: 12,
                        }}
                      >
                        {tag}
                      </span>
                    ))}
                  </div>
                </div>
              </div>
            </div>

            {/* Card 2: Realistic Simulation */}
            <div
              className="bento-card"
              style={{
                background: "#ffffff",
                border: "1.5px solid #ebe8e8",
                borderRadius: 20,
                padding: 28,
                boxShadow: "0 2px 8px rgba(0,0,0,0.05)",
                position: "relative",
                overflow: "hidden",
              }}
            >
              <WavePattern color={STEPS[1].color} />
              <div
                style={{
                  width: 44,
                  height: 44,
                  borderRadius: 12,
                  background: STEPS[1].bgColor,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  marginBottom: 12,
                }}
              >
                <IconBrainCircuit size={22} color={STEPS[1].color} />
              </div>
              <span
                style={{ fontSize: 20, fontWeight: 900, color: STEPS[1].color }}
              >
                02
              </span>
              <h3
                style={{
                  fontSize: 16,
                  fontWeight: 700,
                  color: "#1a1a1a",
                  marginBottom: 8,
                  marginTop: 8,
                }}
              >
                Realistic Simulation
              </h3>
              <p
                style={{
                  fontSize: 13,
                  color: "#6b6d70",
                  lineHeight: 1.6,
                  position: "relative",
                  zIndex: 1,
                }}
              >
                Voice and text modes replicate the real interview environment.
                Choose your pace freely.
              </p>
            </div>

            {/* Card 3: Instant Feedback */}
            <div
              className="bento-card"
              style={{
                background: "#ffffff",
                border: "1.5px solid #ebe8e8",
                borderRadius: 20,
                padding: 28,
                boxShadow: "0 2px 8px rgba(0,0,0,0.05)",
                position: "relative",
                overflow: "hidden",
              }}
            >
              <GridPattern color={STEPS[2].color} />
              <div
                style={{
                  width: 44,
                  height: 44,
                  borderRadius: 12,
                  background: STEPS[2].bgColor,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  marginBottom: 12,
                }}
              >
                <IconClipboardClock size={22} color={STEPS[2].color} />
              </div>
              <span
                style={{ fontSize: 20, fontWeight: 900, color: STEPS[2].color }}
              >
                03
              </span>
              <h3
                style={{
                  fontSize: 16,
                  fontWeight: 700,
                  color: "#1a1a1a",
                  marginBottom: 8,
                  marginTop: 8,
                }}
              >
                Instant Feedback
              </h3>
              <p
                style={{
                  fontSize: 13,
                  color: "#6b6d70",
                  lineHeight: 1.6,
                  position: "relative",
                  zIndex: 1,
                }}
              >
                AI-powered scoring on clarity, confidence, and credibility in
                real time.
              </p>
            </div>

            {/* Card 4: Adaptive Learning (spans 2 cols) */}
            <div
              className="bento-card hiw-card-span2"
              style={{
                background: "#ffffff",
                border: "1.5px solid #ebe8e8",
                borderRadius: 20,
                padding: 28,
                boxShadow: "0 2px 8px rgba(0,0,0,0.05)",
                position: "relative",
                overflow: "hidden",
              }}
            >
              <CircuitPattern color={STEPS[3].color} />
              <div
                style={{ display: "flex", alignItems: "flex-start", gap: 20 }}
              >
                <div
                  style={{
                    width: 44,
                    height: 44,
                    borderRadius: 12,
                    background: STEPS[3].bgColor,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    flexShrink: 0,
                  }}
                >
                  <IconBrainCog size={22} color={STEPS[3].color} />
                </div>
                <div style={{ position: "relative", zIndex: 1 }}>
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: 10,
                      marginBottom: 8,
                    }}
                  >
                    <span
                      style={{
                        fontSize: 20,
                        fontWeight: 900,
                        color: STEPS[3].color,
                      }}
                    >
                      04
                    </span>
                    <h3
                      style={{
                        fontSize: 16,
                        fontWeight: 700,
                        color: "#1a1a1a",
                      }}
                    >
                      Adaptive Learning
                    </h3>
                  </div>
                  <p
                    style={{ fontSize: 13, color: "#6b6d70", lineHeight: 1.6 }}
                  >
                    FiBi's adaptive engine strengthens weak areas and
                    progressively raises the challenge — maximising your
                    readiness score.
                  </p>
                </div>
              </div>
            </div>
          </div>

          <div style={{ textAlign: "center" }}>
            <Button
              label="Start Practicing with FiBi"
              variant="secondary"
              endIcon={<MessageCircleDashed className="h-4 w-4" />}
              onClick={() => navigate(fibiUrl)}
              iconHoverStyle="scale"
            />
          </div>
        </div>
      </section>
    </>
  );
};

export default HowItWorks;
